import { useCallback, useEffect, useRef, useState } from 'react';

import { usePhoneFactorQuery } from 'common/components/ModalMFA/hooks/usePhoneFactorQuery';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useUser } from 'modules/app/hooks/useUser';
import { useNotifications } from 'modules/app/notifications';
import { useCardsAccessQuery } from 'modules/physical-cards/hooks/useCardAccessQuery';
import { useCardAccessRestricted } from 'modules/requests/hooks/useCardAccessRestricted';
import { unwrapQuery } from 'src/core/api/unwrapQuery';

export const useCheckPhoneNumberAndRedirect = () => {
  const { t } = useTranslation('global');
  const { successNotif } = useNotifications();
  const company = useCompany();
  const user = useUser();

  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);
  const [onFactorActivated, setOnFactorActivated] = useState<
    (() => void) | null
  >(null);
  const [isPollingPhoneFactor, setIsPollingPhoneFactor] = useState(false);
  const isPollingPhoneFactorReference = useRef(isPollingPhoneFactor);
  const timeoutReference = useRef<NodeJS.Timeout | undefined>(undefined);

  const phoneFactorQuery = usePhoneFactorQuery(
    isPollingPhoneFactor,
    isPollingPhoneFactor,
  );
  const phoneFactor = unwrapQuery(phoneFactorQuery);

  useEffect(() => {
    isPollingPhoneFactorReference.current = isPollingPhoneFactor;
  }, [isPollingPhoneFactor]);

  useEffect(() => {
    if (phoneFactor?.state === 'active' && isPollingPhoneFactor) {
      // end of the flow, phone number has been activated:
      setIsPollingPhoneFactor(false);
      setIsMfaModalOpen(false);
      if (onFactorActivated) {
        onFactorActivated();
        setOnFactorActivated(null);
      }
    }
  }, [phoneFactor]);

  const cardsAccessQueryState = useCardsAccessQuery(user.id);

  const cardAccessRestricted = useCardAccessRestricted({
    cardsAccess:
      cardsAccessQueryState.status === 'success'
        ? {
            isLoading: false,
            ...cardsAccessQueryState.data,
          }
        : { isLoading: true },
    company,
  });

  const trigger = useCallback(
    (redirectFunction: () => void) => {
      if (
        cardAccessRestricted === 'mfaMissing' ||
        cardAccessRestricted === 'userPhoneMissing'
      ) {
        setOnFactorActivated(() => redirectFunction);
        setIsMfaModalOpen(true);
      } else {
        redirectFunction();
      }
    },
    [cardAccessRestricted],
  );

  const handleMfaModalClose = () => setIsMfaModalOpen(false);

  const handleFactorActivated = () => {
    // start polling the phone factor to make sure we only redirect when it is activated
    setIsPollingPhoneFactor(true);
    if (timeoutReference.current !== null) {
      clearTimeout(timeoutReference.current);
    }
    timeoutReference.current = setTimeout(() => {
      // Although the phone factor is still not activated (timeout),
      // the code was validated => we show a success message, but we don't redirect
      if (isPollingPhoneFactorReference.current) {
        setIsPollingPhoneFactor(false);
        setIsMfaModalOpen(false);
        setOnFactorActivated(null);
        successNotif(t('mfa.modal.code.successTimeout'));
      }
    }, 5000);
  };

  return {
    isMfaModalOpen,
    trigger,
    handleMfaModalClose,
    handleFactorActivated,
  };
};
